import { every } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import { CaseQuery } from 'dashboard/models/case-query';
import { CaseDataManagerSchema, CaseState, StateTag } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { Tag } from 'dashboard/models/tag';
import { Workflow } from 'dashboard/models/workflow';
import CaseActionEditTable from 'dashboard/views/DashboardCaseTemplatesShowView/CaseActionEditTable';
import CaseQueryTable from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/CaseQueryTable';
import { FormAddCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormAddCaseQuery';
import { FormEditCaseQueryData } from 'dashboard/views/DashboardCaseTemplatesShowView/CaseQueryTable/FormEditCaseQuery';
import CaseStatusTable from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable';
import CaseStatusTags from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateSettings/CaseStatusTags';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';

const isValidStatusTags = (statusTags: StateTag[]) => {
  return (
    !statusTags ||
    every(
      statusTags.map((st) => (st['is-tag-required'] ? !(st['available-tags'].length === 0) : true)),
    )
  );
};

export interface FormCaseTemplateSettingsAdvancedProps {
  currentEnvironment: Environment;
  tags: Tag[];
  actionWorkflows: Workflow[];
  caseQueries: CaseQuery[];
  caseDataManagerSchema: CaseDataManagerSchema;
  dataSources: ExternalIntegrationDataSource[];
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  handleCreateCaseQuery: (values: FormAddCaseQueryData) => Promise<void>;
  handleUpdateCaseQuery: (values: FormEditCaseQueryData, id: string) => Promise<void>;
}

const FormCaseTemplateSettingsAdvanced = ({
  currentEnvironment,
  tags,
  actionWorkflows,
  caseDataManagerSchema,
  caseQueries,
  dataSources,
  hasCasesEnhanced,
  hasCasesEnterprise,
  handleCreateCaseQuery,
  handleUpdateCaseQuery,
}: FormCaseTemplateSettingsAdvancedProps) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<{ actions: string[]; states: CaseState[]; 'state-tags': StateTag[] }>();

  const statesFormState: CaseState[] = watch('states');
  const stateTagsFormState: StateTag[] = watch('state-tags');

  return (
    <>
      <Card>
        <Card.Body>
          <Heading size='sm'>Actions</Heading>
          <Paragraph>Customize the actions available on your case.</Paragraph>
          <Controller
            control={control}
            name='actions'
            render={({ field: { onChange, value } }) => (
              <CaseActionEditTable
                currentActionIds={value}
                actionWorkflows={actionWorkflows}
                onConfirm={onChange}
              />
            )}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Controller
            control={control}
            name='states'
            render={({ field: { onChange, value } }) => (
              <CaseStatusTable
                states={value}
                editable={hasCasesEnhanced || hasCasesEnterprise}
                onConfirm={onChange}
                createCaseStatusTag={(state) => {
                  const newStatusTag: StateTag = {
                    'environment-type': currentEnvironment.type,
                    'state-token': state.token,
                    'available-tags': [],
                    'is-tag-required': false,
                    'is-multi-tags-allowed': true,
                    'can-create-tags': false,
                  };

                  onChange(stateTagsFormState.concat([newStatusTag]));
                }}
              />
            )}
          />

          <Spacer height={2} />

          {(hasCasesEnhanced || hasCasesEnterprise) && (
            <>
              <Heading size='sm'>Status tags</Heading>
              <Paragraph>
                Pre-select tags to be available to agents when updating the status of a case.
              </Paragraph>
              <Controller
                control={control}
                name='state-tags'
                rules={{
                  validate: (value) =>
                    isValidStatusTags(value) || 'Please fix errors in case status tags.',
                }}
                render={({ field: { onChange, value } }) => (
                  <CaseStatusTags
                    currentEnvironment={currentEnvironment}
                    states={statesFormState.filter(
                      (state: CaseState) => state['archived-at'] == null,
                    )}
                    allTags={tags}
                    stateTags={value}
                    onChange={onChange}
                  />
                )}
              />
              {errors['state-tags'] && <FormError>{errors['state-tags'].message}</FormError>}
            </>
          )}
        </Card.Body>
      </Card>

      <FeatureFlagged featureFlag='dashboard:case-data-source-query'>
        <Card>
          <Card.Body>
            <CaseQueryTable
              caseQueries={caseQueries}
              dataSources={dataSources}
              caseDataManagerSchema={caseDataManagerSchema}
              handleCreateCaseQuery={handleCreateCaseQuery}
              handleUpdateCaseQuery={handleUpdateCaseQuery}
            />
          </Card.Body>
        </Card>
      </FeatureFlagged>
    </>
  );
};

export default FormCaseTemplateSettingsAdvanced;
